import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../constants/providers/AuthProvider';
import { useCurrentCompany } from '../../hooks/useCurrentCompany';
import { buildScheme } from '../DeepLinkUtils';

export default function AcceptCompanyInvitation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refreshSession } = useAuth();
  const { allCompanies } = useCurrentCompany();
  
  // Get invitation params from URL or restore from sessionStorage (if EULA redirect happened)
  const urlToken = searchParams.get('token');
  const urlDraftStudentId = searchParams.get('draft_student_id');
  
  // Restore invitation context from sessionStorage if URL params are missing
  // This preserves context if user was redirected to EULA and came back
  const [token] = useState<string | null>(() => {
    if (urlToken) {
      // Store in sessionStorage for potential EULA redirect
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('pending_company_invitation_token', urlToken);
      }
      return urlToken;
    }
    // Try to restore from sessionStorage
    if (typeof window !== 'undefined') {
      const stored = sessionStorage.getItem('pending_company_invitation_token');
      if (stored) return stored;
    }
    return null;
  });
  
  const [draftStudentId] = useState<string | null>(() => {
    if (urlDraftStudentId) {
      // Store in sessionStorage for potential EULA redirect
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('pending_company_invitation_draft_student_id', urlDraftStudentId);
      }
      return urlDraftStudentId;
    }
    // Try to restore from sessionStorage
    if (typeof window !== 'undefined') {
      const stored = sessionStorage.getItem('pending_company_invitation_draft_student_id');
      if (stored) return stored;
    }
    return null;
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showContinueWeb, setShowContinueWeb] = useState(false);
  const [_validated, setValidated] = useState(false);
  const [redirectingToLogin, setRedirectingToLogin] = useState(false);
  const [redirectingToApp, setRedirectingToApp] = useState(false);

  // Extract validation logic into reusable function
  const validateAndProcessInvitation = useCallback(async () => {
    if (!token || !draftStudentId) {
      setError('Invalid invitation link. Missing required parameters.');
      setLoading(false);
      return false;
    }

    // Check if user is logged in first
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      // Show explanatory message before redirecting to login
      setRedirectingToLogin(true);
      setLoading(false);
      // Redirect to login with invitation URL preserved so user can accept after login
      const invitationUrl = `/accept-company-invitation?token=${encodeURIComponent(token || '')}&draft_student_id=${encodeURIComponent(draftStudentId || '')}`;
      // Small delay to show the message, then redirect
      setTimeout(() => {
        navigate(`/signin?redirect=${encodeURIComponent(invitationUrl)}`);
      }, 2000);
      return false;
    }

    // Validate invitation token matches draft student record
    const { data: draftStudent, error: draftError } = await supabase
      .from('staging_import_members')
      .select('id, user_id, company_id, status, invite_token, invite_sent_at, companies(name)')
      .eq('id', draftStudentId)
      .eq('invite_token', token)
      .single();

    if (draftError || !draftStudent) {
      setError('Invalid or expired invitation link.');
      setLoading(false);
      return false;
    }

    // Check if invitation belongs to current user
    if (draftStudent.user_id !== session.user.id) {
      setError('This invitation does not belong to your account.');
      setLoading(false);
      return false;
    }

    // Check if invitation is still valid
    if (draftStudent.status !== 'invited') {
      setError('This invitation has already been processed.');
      setLoading(false);
      return false;
    }

    // Check if invitation has expired (7 days)
    // Only check expiration if invite_sent_at is set
    // If invite_sent_at is NULL, the invitation hasn't been sent yet, so it can't be expired
    if (draftStudent.invite_sent_at) {
      try {
        const inviteDate = new Date(draftStudent.invite_sent_at);
        // Validate the date is valid
        if (isNaN(inviteDate.getTime())) {
          // Don't fail on invalid date - treat as not expired
        } else {
          const expiryDate = new Date(inviteDate.getTime() + 7 * 24 * 60 * 60 * 1000);
          const now = new Date();
          
          if (now > expiryDate) {
            setError('This invitation has expired. Please request a new invitation.');
            setLoading(false);
            return false;
          }
        }
      } catch {
        // Don't fail on date parsing errors - treat as not expired
      }
    }

    // Validation successful - show accept button
    setValidated(true);
    setLoading(false);
    setShowContinueWeb(true);
    return true;
  }, [token, draftStudentId, navigate]);

  useEffect(() => {
    const handleInvitation = async () => {
      // Handle magic link authentication if present in URL hash
      // Magic links redirect here with hash params: #access_token=...&refresh_token=...&type=magiclink
      const hash = window.location.hash;
      let magicLinkAuthenticated = false;
      
      if (hash) {
        const hashParams = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const type = hashParams.get('type');
        
        if (accessToken && refreshToken && type === 'magiclink') {
          try {
            // Set session from magic link
            const { error: sessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });
            
            if (sessionError) {
              setError('Invalid or expired magic link. Please try again.');
              setLoading(false);
              return;
            }
            
            // Store invitation context in sessionStorage before any potential EULA redirect
            // This preserves the invitation so user can accept after EULA
            if (token && draftStudentId && typeof window !== 'undefined') {
              sessionStorage.setItem('pending_company_invitation_token', token);
              sessionStorage.setItem('pending_company_invitation_draft_student_id', draftStudentId);
            }
            
            // Clear hash from URL immediately to prevent re-processing
            window.history.replaceState(null, '', window.location.pathname + window.location.search);
            
            // Refresh session to get updated user metadata
            await refreshSession();
            
            // Wait a moment for session to propagate
            await new Promise(resolve => setTimeout(resolve, 300));
            
            magicLinkAuthenticated = true;
          } catch {
            setError('Failed to authenticate. Please try again.');
            setLoading(false);
            return;
          }
        }
      }
      
      if (!token || !draftStudentId) {
        setError('Invalid invitation link. Missing required parameters.');
        setLoading(false);
        return;
      }
      
      // If magic link authenticated, ensure we validate the invitation immediately
      // This ensures the accept button shows up right away
      if (magicLinkAuthenticated) {
        await validateAndProcessInvitation();
        return;
      }

      // CRITICAL: Check if mobile FIRST before any validation/processing
      // This prevents consuming the invitation on web if app is installed
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const isLocalhost = typeof window !== 'undefined' && 
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      
      if (isMobile) {
        // On mobile, show "Opening app..." UI and redirect to app
        // Use Universal Link for production, custom scheme for development
        setRedirectingToApp(true);
        setLoading(false);
        
        // Development: Use custom scheme (rollops-dev://)
        // Production: Use Universal Link (https://rollops.pro)
        const deepLink = isLocalhost
          ? buildScheme(`/accept-company-invitation?token=${encodeURIComponent(token)}&draft_student_id=${encodeURIComponent(draftStudentId)}`)
          : `https://rollops.pro/accept-company-invitation?token=${encodeURIComponent(token)}&draft_student_id=${encodeURIComponent(draftStudentId)}`;
        
        // On localhost, show terminal commands immediately (don't redirect yet)
        // This allows developers to test in simulator without the redirect interfering
        if (isLocalhost) {
          // Show "Continue on Web" immediately on localhost so terminal commands are visible
          setShowContinueWeb(true);
          // Don't auto-redirect on localhost - let developer use terminal commands
        } else {
          // Production: Try deep link (works if app is installed)
          // If app is installed, this will open the app and user leaves the page
          // If app is not installed, browser will stay on web (for Universal Links)
          window.location.href = deepLink;
          
          // After a delay, if still on page (app didn't open), show option to continue on web
          setTimeout(() => {
            // If we're still on the page, app likely didn't open
            // Show option to continue on web
            setShowContinueWeb(true);
          }, 2000);
        }
        
        // Don't validate or process on web yet - wait to see if app opens
        // If user clicks "Continue on Web", we'll validate then
        return;
      }

      // Desktop: validate and process immediately
      await validateAndProcessInvitation();
    };

    handleInvitation();
  }, [token, draftStudentId, validateAndProcessInvitation, refreshSession]);

  const handleAccept = async () => {
    if (!token || !draftStudentId || !user?.id) {
      setError('Missing required information to accept invitation.');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Call the RPC function to accept the invitation
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error: rpcError } = await (supabase.rpc as any)('accept_company_invitation', {
        p_draft_student_id: draftStudentId,
      });

      if (rpcError) {
        throw rpcError;
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to accept invitation');
      }

      const companyId = data.company_id;

      if (!companyId) {
        throw new Error('No company ID returned from invitation acceptance');
      }

      // Clear stored invitation context since invitation is being accepted
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('pending_company_invitation_token');
        sessionStorage.removeItem('pending_company_invitation_draft_student_id');
      }

      // Update last_selected_company to switch to the new gym
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ last_selected_company: companyId })
        .eq('id', user.id);

      if (updateError) {
        throw new Error('Failed to update company selection');
      }

      // Refresh JWT session to get updated company_id claim for RLS
      await refreshSession();

      // Wait a moment for JWT to propagate
      await new Promise(resolve => setTimeout(resolve, 500));

      // Check user's role after accepting invitation
      const { data: relationships, error: relError } = await supabase
        .from('user_company_relationships')
        .select(`
          company_id,
          system_roles (
            role
          )
        `)
        .eq('user_id', user.id)
        .eq('company_id', companyId);

      if (relError) {
        // Default to dashboard if we can't determine role
        setSuccess(true);
        setTimeout(() => {
          if (allCompanies.length > 1) {
            navigate('/company-selector');
          } else {
            navigate('/dashboard');
          }
        }, 2000);
        return;
      }

      // Check role ONLY in the newly accepted company (not other companies)
      // IMPORTANT: All invites are for students only - roles are upgraded after joining
      // This check is a safety measure, but invites should always result in student role
      const relationship = relationships?.[0] as {
        system_roles?: { role: string } | null;
      } | undefined;
      const role = relationship?.system_roles?.role;

      setSuccess(true);

      // Route based on role in THIS company only
      setTimeout(() => {
        if (role === 'owner' || role === 'admin') {
          // This shouldn't happen for invites (all invites are students), but safety check
          if (allCompanies.length > 1) {
            navigate('/company-selector');
          } else {
            navigate('/dashboard');
          }
        } else {
          // Student/coach/reception or no role yet - route to student landing
          // All invites are students, so this is the expected path
          navigate('/student-landing');
        }
      }, 2000);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to accept invitation. Please try again.';
      setError(errorMessage);
      setProcessing(false);
    }
  };

  // Mobile: Show "Opening app..." UI with option to continue on web
  if (redirectingToApp) {
    const isLocalhost = typeof window !== 'undefined' && 
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
    const deepLink = isLocalhost
      ? buildScheme(`/accept-company-invitation?token=${encodeURIComponent(token || '')}&draft_student_id=${encodeURIComponent(draftStudentId || '')}`)
      : `https://rollops.pro/accept-company-invitation?token=${encodeURIComponent(token || '')}&draft_student_id=${encodeURIComponent(draftStudentId || '')}`;
    
    return (
      <>
        <Helmet>
          <title>Accept Invitation - RollOps</title>
          <meta name="robots" content="noindex, nofollow, noimageindex, nosnippet, noarchive" />
          <meta name="googlebot" content="noindex, nofollow, noimageindex, nosnippet, noarchive, notranslate" />
          <meta name="bingbot" content="noindex, nofollow, noimageindex, nosnippet, noarchive" />
        </Helmet>
        <div className="min-h-screen bg-[#111010] flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardContent className="pt-6">
              <div className="flex flex-col items-center justify-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e11d48] mb-4" />
                <h2 className="text-xl font-semibold text-white mb-2">Opening RollOps App</h2>
              <p className="text-gray-400 text-sm text-center mb-4">
                We&apos;re opening the RollOps app to accept your invitation.
              </p>
              {showContinueWeb ? (
                <div className="w-full mt-4">
                  <p className="text-gray-500 text-xs text-center mb-4">
                    App didn&apos;t open? Continue on web instead.
                  </p>
                  <Button
                    onClick={async () => {
                      // Now validate and process on web
                      setRedirectingToApp(false);
                      setLoading(true);
                      await validateAndProcessInvitation();
                    }}
                    variant="outline"
                    className="w-full border-[#504A4A] text-[#EBFFEE] hover:bg-[#363232] hover:text-[#EBFFEE] bg-transparent"
                  >
                    Continue on Web
                  </Button>
                </div>
              ) : null}
              
              {/* DEV ONLY: Show copyable deep link and terminal command for simulator testing */}
              {isLocalhost && (
                <div className="mt-6 w-full p-4 bg-[#1a1717] border border-[#504A4A] rounded-lg">
                  <p className="text-xs font-mono text-rollops-accent mb-2">🛠️ DEV MODE - Copy for Simulator:</p>
                  
                  {/* Terminal command for xcrun */}
                  <div className="mb-3">
                    <p className="text-xs text-[#A9A3A3] mb-1">iOS Simulator command:</p>
                    <div className="relative group">
                      <pre className="text-xs font-mono text-[#EBFFEE] bg-[#111010] p-2 rounded overflow-x-auto whitespace-pre-wrap break-all">
                        {`xcrun simctl openurl booted "${deepLink}"`}
                      </pre>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(`xcrun simctl openurl booted "${deepLink}"`);
                          alert('Copied terminal command!');
                        }}
                        className="absolute top-1 right-1 px-2 py-1 text-xs bg-[#9B1421] text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        Copy
                      </button>
                    </div>
                  </div>

                  {/* Android command */}
                  <div className="mb-3">
                    <p className="text-xs text-[#A9A3A3] mb-1">Android Emulator command:</p>
                    <div className="relative group">
                      <pre className="text-xs font-mono text-[#EBFFEE] bg-[#111010] p-2 rounded overflow-x-auto whitespace-pre-wrap break-all">
                        {`adb shell am start -a android.intent.action.VIEW -d "${deepLink}"`}
                      </pre>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(`adb shell am start -a android.intent.action.VIEW -d "${deepLink}"`);
                          alert('Copied terminal command!');
                        }}
                        className="absolute top-1 right-1 px-2 py-1 text-xs bg-[#9B1421] text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        Copy
                      </button>
                    </div>
                  </div>

                  {/* Raw deep link */}
                  <div>
                    <p className="text-xs text-[#A9A3A3] mb-1">Deep link URL:</p>
                    <div className="relative group">
                      <pre className="text-xs font-mono text-[#EBFFEE] bg-[#111010] p-2 rounded overflow-x-auto whitespace-pre-wrap break-all max-h-32 overflow-y-auto">
                        {deepLink}
                      </pre>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(deepLink);
                          alert('Copied deep link!');
                        }}
                        className="absolute top-1 right-1 px-2 py-1 text-xs bg-[#9B1421] text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                </div>
              )}
              </div>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  if (redirectingToLogin) {
    return (
      <>
        <Helmet>
          <title>Accept Invitation - RollOps</title>
          <meta name="robots" content="noindex, nofollow, noimageindex, nosnippet, noarchive" />
          <meta name="googlebot" content="noindex, nofollow, noimageindex, nosnippet, noarchive, notranslate" />
          <meta name="bingbot" content="noindex, nofollow, noimageindex, nosnippet, noarchive" />
        </Helmet>
        <div className="min-h-screen bg-[#111010] flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e11d48] mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Login Required</h2>
              <p className="text-gray-400 text-sm text-center mb-2">
                You need to be logged in to accept this invitation.
              </p>
              <p className="text-gray-500 text-xs text-center">
                Redirecting you to login... You&apos;ll be brought back here after signing in.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Accept Invitation - RollOps</title>
          <meta name="robots" content="noindex, nofollow, noimageindex, nosnippet, noarchive" />
          <meta name="googlebot" content="noindex, nofollow, noimageindex, nosnippet, noarchive, notranslate" />
          <meta name="bingbot" content="noindex, nofollow, noimageindex, nosnippet, noarchive" />
        </Helmet>
        <div className="min-h-screen bg-[#111010] flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardContent className="pt-6">
              <div className="flex flex-col items-center justify-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e11d48] mb-4" />
                <h2 className="text-xl font-semibold text-white mb-2">Processing Invitation</h2>
                <p className="text-gray-400 text-sm">Validating your invitation...</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>Accept Invitation - RollOps</title>
          <meta name="robots" content="noindex, nofollow, noimageindex, nosnippet, noarchive" />
          <meta name="googlebot" content="noindex, nofollow, noimageindex, nosnippet, noarchive, notranslate" />
          <meta name="bingbot" content="noindex, nofollow, noimageindex, nosnippet, noarchive" />
        </Helmet>
        <div className="min-h-screen bg-[#111010] flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-red-500">Invitation Error</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-300 mb-6">{error}</p>
              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    // Preserve invitation URL when redirecting to login
                    const currentUrl = window.location.pathname + window.location.search;
                    navigate(`/signin?redirect=${encodeURIComponent(currentUrl)}`);
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Go to Login
                </Button>
                <Button
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="flex-1"
                >
                  Go Home
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  if (success) {
    return (
      <>
        <Helmet>
          <title>Accept Invitation - RollOps</title>
          <meta name="robots" content="noindex, nofollow, noimageindex, nosnippet, noarchive" />
          <meta name="googlebot" content="noindex, nofollow, noimageindex, nosnippet, noarchive, notranslate" />
          <meta name="bingbot" content="noindex, nofollow, noimageindex, nosnippet, noarchive" />
        </Helmet>
        <div className="min-h-screen bg-[#111010] flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center py-8">
              <div className="rounded-full bg-green-500/20 p-4 mb-4">
                <svg
                  className="w-12 h-12 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Invitation Accepted!</h2>
              <p className="text-gray-400 text-sm text-center">
                Redirecting you to your new gym&hellip;
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
      </>
    );
  }

  // Desktop view - show accept button
  return (
    <>
      <Helmet>
        <title>Accept Invitation - RollOps</title>
        <meta name="robots" content="noindex, nofollow, noimageindex, nosnippet, noarchive" />
        <meta name="googlebot" content="noindex, nofollow, noimageindex, nosnippet, noarchive, notranslate" />
        <meta name="bingbot" content="noindex, nofollow, noimageindex, nosnippet, noarchive" />
      </Helmet>
      <div className="min-h-screen bg-[#0b1016] flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-white">Accept Company Invitation</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-300 mb-6">
            You&apos;ve been invited to join a new gym. Click the button below to accept the invitation.
          </p>
          <div className="flex gap-3">
            <Button
              onClick={handleAccept}
              disabled={processing}
              className="flex-1 bg-[#e11d48] hover:bg-[#be1e3e]"
            >
              {processing ? 'Accepting...' : 'Accept Invitation'}
            </Button>
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              disabled={processing}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
    </>
  );
}

